import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, Star, X, Sparkles, Users } from "lucide-react";
import { particuliers } from "../mock/servicesData";
import QuoteForm from "../components/QuoteForm";
import Reveal from "../components/Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const Particuliers = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="pt-16 sm:pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={particuliers.hero.image} alt="Déménagement résidentiel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#131f38]/95 to-[#1B2A4A]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-2xl animate-fadeUp">
            <span className="inline-block bg-[#C9A227] text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">Particuliers</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{particuliers.hero.title}</h1>
            <p className="mt-5 text-lg text-gray-200 max-w-xl">{particuliers.hero.subtitle}</p>
            <button onClick={() => navigate("/contact")} className="mt-8 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors shadow-lg">
              Demander un devis gratuit <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {particuliers.intro.map((p, i) => (
            <p key={i} className="text-gray-600 leading-relaxed mb-4 text-lg">{p}</p>
          ))}
        </div>
      </section>

      {/* FORMULES */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Nos formules</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-4">Choisissez la formule adaptée</h2>
            <p className="text-gray-600">Tous nos devis sont gratuits et sans engagement.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {particuliers.formules.map((f, idx) => (
              <Reveal
                key={f.id}
                delay={idx * 100}
                className={`relative bg-white rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col ${
                  f.popular ? "border-[#C9A227] shadow-lg ring-1 ring-[#C9A227]/30" : "border-gray-100 shadow-sm"
                }`}
              >
                {f.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star size={12} className="fill-white" /> Populaire
                  </span>
                )}
                <h3 className="text-xl font-bold text-[#1B2A4A]">{f.name}</h3>
                <p className="text-sm font-medium text-[#C9A227] mt-1">{f.tagline}</p>
                <p className="text-sm text-gray-600 mt-3">{f.desc}</p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {f.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check size={16} className="text-[#C9A227] mt-0.5 shrink-0" /> {feat}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelected(f)}
                  className={`mt-6 w-full font-semibold py-2.5 rounded-lg transition-colors ${
                    f.popular ? "bg-[#1B2A4A] hover:bg-[#26406b] text-white" : "border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white"
                  }`}
                >
                  Voir détails
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Étapes</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2">Les étapes de votre déménagement</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {particuliers.steps.map((s, idx) => (
              <Reveal key={s.num} delay={idx * 100} className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-xl font-bold shadow-md">
                  {s.num}
                </div>
                <h3 className="mt-4 font-bold text-[#1B2A4A]">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Pourquoi nous choisir</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-8">Un service de confiance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {particuliers.whyChoose.map((w, i) => {
                const Icon = Icons[w.icon] || Icons.Check;
                return (
                  <div key={i} className="flex gap-3">
                    <div className="w-11 h-11 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center shrink-0">
                      <Icon size={22} className="text-[#1B2A4A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1B2A4A]">{w.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{w.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <img src={particuliers.image} alt="Couple déménageant" className="rounded-2xl shadow-xl w-full h-[460px] object-cover" />
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-3">Demandez votre devis gratuit</h2>
            <p className="text-gray-600">Obtenez une estimation personnalisée pour votre déménagement.</p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* FORMULE DETAILS DIALOG */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  {selected.popular && (
                    <span className="inline-flex items-center gap-1 bg-[#C9A227] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                      <Star size={11} className="fill-white" /> Populaire
                    </span>
                  )}
                  <span className="text-xs font-semibold text-[#C9A227] uppercase tracking-wider">Formule</span>
                </div>
                <DialogTitle className="text-2xl font-bold text-[#1B2A4A]">{selected.name}</DialogTitle>
                <p className="text-sm font-medium text-[#C9A227]">{selected.tagline}</p>
              </DialogHeader>

              <p className="text-gray-600 text-sm leading-relaxed mt-2">{selected.longDesc}</p>

              <div className="mt-3 flex items-start gap-2 bg-[#1B2A4A]/5 rounded-lg p-3">
                <Users size={18} className="text-[#1B2A4A] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-[#1B2A4A]">Idéal pour</p>
                  <p className="text-sm text-gray-600">{selected.idealFor}</p>
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-[#1B2A4A] flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-[#C9A227]" /> Inclus dans la formule
                </h4>
                <ul className="space-y-2">
                  {selected.included.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check size={16} className="text-green-600 mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              {selected.notIncluded && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-500 mb-2">Non inclus</h4>
                  <ul className="space-y-2">
                    {selected.notIncluded.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                        <X size={16} className="text-gray-400 mt-0.5 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => {
                  setSelected(null);
                  navigate(`/contact?formule=${encodeURIComponent(selected.name)}`);
                }}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-sm"
              >
                Choisir cette formule & demander un devis <ArrowRight size={18} />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Particuliers;
